import {CommonModule} from '@angular/common';
import {ChangeDetectionStrategy, Component, inject, Input} from '@angular/core';
import {filterNullishRx, TRANSLATE, TranslatePipe} from '@brunata-angular/util';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';
import {combineLatest, map, Observable, ReplaySubject, startWith, switchMap} from 'rxjs';
import {
    AgentMeterSettingsData,
    AgentMeterSettingsFormGroup,
    MetersForAgentData,
    SelectMetersForAgentFormGroup,
} from './select-meters-for-agent.model';
import {SelectMetersSettingsComponent} from './select-meters-settings/select-meters-settings.component';
import {SelectMetersForAgentTableComponent} from './select-meters-table/select-meters-for-agent-table.component';
import {SelectMetersForAgentService} from './select-meters-for-agent.service';
import {SelectableAgentMeter} from './select-meters-table/select-meters-for-agent-table.model';
import {uniqBy} from 'lodash-es';
import {ConfirmationDialogService} from '@brunata-angular/ui';
import {ReactiveFormsModule} from '@angular/forms';
import {ConsumptionToMonitor} from '../consumption-agent-monitoring/consumption-agent-monitoring.model';

type SelectMetersForAgentView = SelectMetersForAgentFormGroup['controls'] & {
    formGroup: SelectMetersForAgentFormGroup;
    selectableMeters: SelectableAgentMeter[];
    settings: AgentMeterSettingsData;
    settingsPending: boolean;
    reportId?: string;
    singleMeterWarning: boolean;
};

@Component({
    selector: 'app-select-meters-for-agent',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        SelectMetersSettingsComponent,
        SelectMetersForAgentTableComponent,
        TranslatePipe,
    ],
    templateUrl: './select-meters-for-agent.component.html',
    styleUrl: './select-meters-for-agent.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SelectMetersForAgentComponent {
    @Input({required: true}) set formGroup(formGroup: SelectMetersForAgentFormGroup) {
        this.formGroupSubject$.next(formGroup);
    }

    @Input({required: true}) set metersForAgentData(metersForAgentData: MetersForAgentData) {
        this.metersForAgentDataSubject$.next(metersForAgentData);
    }

    @Input({required: true}) consumptionToMonitor!: ConsumptionToMonitor;

    private readonly translate = inject(TRANSLATE);
    private readonly confirmationDialogService = inject(ConfirmationDialogService);
    private readonly activeModal = inject(NgbActiveModal);
    private readonly metersForAgentService = inject(SelectMetersForAgentService);

    private readonly formGroupSubject$ = new ReplaySubject<
        SelectMetersForAgentFormGroup | undefined
    >(1);

    private readonly formGroup$ = this.formGroupSubject$.pipe(filterNullishRx());

    private readonly formGroupChange$: Observable<SelectMetersForAgentFormGroup> =
        this.formGroup$.pipe(
            switchMap(formGroup =>
                formGroup.valueChanges.pipe(
                    startWith(formGroup),
                    map(() => formGroup),
                ),
            ),
        );

    private readonly metersForAgentDataSubject$ = new ReplaySubject<MetersForAgentData | undefined>(
        1,
    );

    private readonly metersForAgentData$: Observable<MetersForAgentData> =
        this.metersForAgentDataSubject$.pipe(filterNullishRx());

    private readonly meters$ = this.metersForAgentData$.pipe(map(({meters}) => meters));

    private readonly selectableMeters$ = combineLatest([
        this.meters$,
        this.formGroupChange$.pipe(map(formGroup => formGroup.getRawValue())),
    ]).pipe(
        map(([allMeters, {meterSettingsFormGroup}]) => {
            const selectedUsageAndUnits = meterSettingsFormGroup.selectableUsageAndUnits.filter(
                usageAndUnit => usageAndUnit.selected === true,
            );

            return allMeters.filter(
                meter =>
                    meter.meterCriteriaMismatch ||
                    selectedUsageAndUnits.some(({name}) => meter.usageAndUnit === name),
            );
        }),
    );

    readonly view$: Observable<SelectMetersForAgentView> = combineLatest([
        this.formGroup$,
        this.selectableMeters$,
        this.metersForAgentData$.pipe(map(({settings}) => settings)),
        this.metersForAgentService.meterTableVisible$,
    ]).pipe(
        map(([formGroup, selectableMeters, settings, metersTableVisible]) => {
            const selectedUsageAndUnits = formGroup.controls.meterSettingsFormGroup
                .getRawValue()
                .selectableUsageAndUnits.filter(item => item.selected === true)
                .map(item => item.name);

            const availableMeters = selectableMeters.filter(
                meter =>
                    meter.meterCriteriaMismatch === false &&
                    selectedUsageAndUnits.some(usage => meter.usageAndUnit === usage),
            );

            const isComparisonType = this.consumptionToMonitor === 'DIFFERENCECONSUMPTION';

            return {
                ...formGroup.controls,
                formGroup,
                selectableMeters,
                settings,
                settingsPending:
                    formGroup.controls.meterSettingsFormGroup.invalid ||
                    metersTableVisible === false,
                singleMeterWarning: availableMeters.length === 1 && isComparisonType,
            };
        }),
    );

    submit(formGroup: SelectMetersForAgentFormGroup): void {
        formGroup.markAllAsTouched();
        formGroup.controls.submitted.setValue(true);

        if (formGroup.invalid) {
            return;
        }

        this.activeModal.close();
    }

    showMeters(meters: SelectableAgentMeter[], formGroup: AgentMeterSettingsFormGroup): void {
        formGroup.markAllAsTouched();

        if (formGroup.invalid) {
            return;
        }

        const {tableFormGroup, meterTableVisible$} = this.metersForAgentService;

        const uniqueMeters = uniqBy(meters, 'usageAndUnit');

        tableFormGroup.controls.filterFormGroup.patchValue({
            usageAndUnit: uniqueMeters.map(meter => ({
                name: meter.usageAndUnit,
                selected: false,
            })),
            meterModel: uniqBy(meters, 'meterModel').map(meter => ({
                name: meter.meterModel,
                selected: false,
            })),
        });

        formGroup.controls.selectedUsageAndUnits.setValue(
            uniqueMeters.map(({alloUnitType, unit}) => ({
                alloUnitType,
                unit,
            })),
        );
        meterTableVisible$.next(true);
    }

    reset(formGroup: SelectMetersForAgentFormGroup): void {
        void this.confirmationDialogService
            .confirm(
                this.translate('common_warning', 'online', 'common'),
                this.translate('consumption-agents_create_reset_meter_settings'),
            )
            .then(confirmed => {
                if (!confirmed) {
                    return;
                }
                this.metersForAgentService.resetState();
                formGroup.reset();
            });
    }

    dismiss(): void {
        this.activeModal.dismiss();
    }
}
